"use client";

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import * as LucideIcons from "lucide-react";
import Loader from "@/components/Loader";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchServices } from "@/store/slices/landingSlice";
import Link from "next/link";
import { slugify } from "@/utils/slugify";

// ─── Animation variants ───────────────────────────────────────────────────────

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.55,
      ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
      delay: i * 0.08,
    },
  }),
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: (i = 0) => ({
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: [0.0, 0.0, 0.2, 1] as [number, number, number, number],
      delay: i * 0.06,
    },
  }),
};

// ─── Marquee Strip ────────────────────────────────────────────────────────────

const MARQUEE_REPEAT = 30;

const MarqueeTrack = ({
  reverse = false,
  bgClass,
  textClass,
  iconColor,
}: {
  reverse?: boolean;
  bgClass: string;
  textClass: string;
  iconColor: string;
}) => {
  const items = Array.from({ length: MARQUEE_REPEAT * 2 });
  return (
    <div className="overflow-hidden">
      <motion.div
        className={`flex items-center ${bgClass}`}
        style={{ width: "max-content" }}
        animate={{ x: reverse ? ["-50%", "0%"] : ["0%", "-50%"] }}
        transition={{ duration: reverse ? 80 : 70, repeat: Infinity, ease: "linear" }}
      >
        {items.map((_, i) => (
          <div
            key={i}
            className={`inline-flex items-center gap-4 px-10 py-4 font-extrabold text-base md:text-lg uppercase tracking-[0.22em] whitespace-nowrap select-none ${textClass}`}
          >
            <LucideIcons.Sparkles className={`w-4 h-4 shrink-0 ${iconColor}`} />
            Built on Intelligence
            <span className={`mx-1 opacity-40 ${iconColor}`}>◆</span>
          </div>
        ))}
      </motion.div>
    </div>
  );
};

const MarqueeBlock = () => {
  const stripStyle: React.CSSProperties = {
    width: "300vw",
    marginLeft: "-100vw",
    position: "relative",
  };

  return (
    <div className="relative py-32" style={{ isolation: "isolate" }}>
      <div style={{ ...stripStyle, transform: "rotate(-7deg)", marginBottom: "8px" }}>
        <MarqueeTrack
          bgClass="bg-gradient-to-r from-[#0D0047] via-[#2B0096] to-[#6B00F0]"
          textClass="text-white/90"
          iconColor="text-[#C4B5FD]"
        />
      </div>
      <div style={{ ...stripStyle, transform: "rotate(7deg)", marginTop: "8px" }}>
        <MarqueeTrack
          reverse
          bgClass="bg-gradient-to-r from-[#7C00FF] via-[#5500CC] to-[#2B0096]"
          textClass="text-white/90"
          iconColor="text-[#E9D5FF]"
        />
      </div>
    </div>
  );
};

// ─── Service Card ─────────────────────────────────────────────────────────────

const ServiceCard = ({
  service,
  index,
  mode,
}: {
  service: any;
  index: number;
  mode: "light" | "dark";
}) => {
  // @ts-ignore
  const Icon = LucideIcons[service.icon] || LucideIcons.Zap;

  return (
    <motion.div
      custom={index}
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.12 }}
      whileHover={{ y: -10, transition: { duration: 0.3, ease: "easeOut" } }}
      className="group relative h-full"
    >
      <Link href={`/services/${slugify(service.title)}`} className="h-full">
        <div
          className={[
            "relative h-full cursor-pointer flex flex-col justify-between overflow-hidden",
            "rounded-[2rem] border backdrop-blur-md p-8",
            "transition-[border-color,box-shadow,background-color] duration-300",
            mode === "dark"
              ? "bg-[#0B0F19]/50 border-white/10 hover:border-[#8B31FF]/30 hover:shadow-[0_0_30px_rgba(79,17,194,0.15)]"
              : "bg-white/70 border-gray-200 hover:border-[#8B31FF]/30 hover:shadow-[0_10px_30px_rgba(79,17,194,0.08)]",
          ].join(" ")}
        >
          {/* Hover gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#8B31FF]/0 via-[#8B31FF]/5 to-[#8B31FF]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-[2rem]" />

          {/* Ambient glow */}
          <div
            className={[
              "absolute -right-20 -top-20 h-60 w-60 rounded-full blur-[80px]",
              "transition-all duration-500 group-hover:scale-125",
              mode === "dark"
                ? "bg-[#8B31FF]/10 group-hover:bg-[#8B31FF]/20"
                : "bg-[#8B31FF]/5 group-hover:bg-[#8B31FF]/10",
            ].join(" ")}
          />

          {/* Decorative large icon */}
          <div className="absolute bottom-[-10%] right-[-10%] opacity-[0.03] transition-transform duration-700 group-hover:rotate-12 group-hover:scale-125 pointer-events-none">
            <Icon className={`h-64 w-64 ${mode === "dark" ? "text-[#8B31FF]" : "text-[#8B31FF]"}`} />
          </div>

          <div className="relative z-10">
            {/* Icon */}
            <div className="relative mb-8 inline-flex items-center justify-center">
              <div
                className={[
                  "absolute inset-0 blur-xl rounded-full transition-colors duration-300",
                  mode === "dark"
                    ? "bg-[#8B31FF]/20 group-hover:bg-[#8B31FF]/20"
                    : "bg-[#8B31FF]/10 group-hover:bg-[#8B31FF]/20",
                ].join(" ")}
              />
              <div
                className={[
                  "relative h-16 w-16 rounded-lg border flex items-center justify-center",
                  "transition-[border-color,background-color] duration-300 shadow-lg",
                  mode === "dark"
                    ? "bg-[#131620] border-white/10 group-hover:border-[#8B31FF]/50"
                    : "bg-white border-gray-100 group-hover:border-[#8B31FF]/30",
                ].join(" ")}
              >
                <Icon
                  className={[
                    "h-8 w-8 transition-colors duration-300",
                    mode === "dark"
                      ? "text-[#8B31FF] group-hover:text-white"
                      : "text-[#8B31FF] group-hover:text-[#8B31FF]",
                  ].join(" ")}
                />
              </div>
            </div>

            {/* Title */}
            <h3
              className={[
                "mb-4 text-2xl font-bold transition-colors duration-300",
                mode === "dark"
                  ? "text-white group-hover:text-[#8B31FF]"
                  : "text-gray-900 group-hover:text-[#8B31FF]",
              ].join(" ")}
            >
              {service.title}
            </h3>

            {/* Description */}
            <p
              className={[
                "mb-8 leading-relaxed transition-colors duration-300",
                mode === "dark"
                  ? "text-gray-400 group-hover:text-gray-300"
                  : "text-gray-600 group-hover:text-gray-700",
              ].join(" ")}
            >
              {service.description}
            </p>
          </div>

          {/* Action row */}
          <div
            className={[
              "mt-auto pt-6 border-t flex items-center justify-between relative z-10",
              "transition-colors duration-300",
              mode === "dark" ? "border-white/5" : "border-gray-100",
            ].join(" ")}
          >
            <span
              className={`text-sm font-semibold tracking-wide uppercase ${
                mode === "dark" ? "text-[#8B31FF]" : "text-[#8B31FF]"
              }`}
            >
              Learn More
            </span>
            <motion.div
              whileHover={{ x: 5 }}
              transition={{ duration: 0.2 }}
              className={[
                "flex h-10 w-10 items-center justify-center rounded-full border",
                "transition-[background-color,border-color,color] duration-300",
                mode === "dark"
                  ? "bg-white/5 border-white/10 text-white group-hover:bg-[#8B31FF] group-hover:border-[#8B31FF] group-hover:text-black"
                  : "bg-gray-50 border-gray-200 text-gray-700 group-hover:bg-[#8B31FF] group-hover:border-[#8B31FF] group-hover:text-white",
              ].join(" ")}
            >
              <LucideIcons.ArrowRight className="h-4 w-4" />
            </motion.div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

// ─── Main Section ─────────────────────────────────────────────────────────────

const ServicesSection = () => {
  const dispatch = useAppDispatch();
  const { data: services, loading } = useAppSelector((state) => state.landing.services);
  const { mode } = useAppSelector((state) => state.theme);

  // Prevent transition-colors from animating on first paint.
  // After mount, enable it for smooth theme-toggle transitions only.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    dispatch(fetchServices());
  }, [dispatch]);

  const bg = mode === "dark" ? "bg-[#0B0F19]" : "bg-slate-50";
  // Only add transition-colors AFTER mount — never on the initial paint
  const transitionCls = mounted ? "transition-colors duration-300" : "";

  if (loading) {
    return (
      <section className={`min-h-[50vh] flex items-center justify-center ${bg} ${transitionCls}`}>
        <Loader loading={loading} services={services} mode={mode} duration={2000} />
      </section>
    );
  }

  return (
    <section className={`relative overflow-hidden pt-32 pb-2 ${bg} ${transitionCls}`}>

      {/* ── Background decoration ── */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Grid */}
        <div
          className={`absolute inset-0 ${transitionCls} ${
            mode === "dark" ? "opacity-[0.05]" : "opacity-[0.03]"
          }`}
          style={{
            backgroundImage:
              mode === "dark"
                ? `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`
                : `linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />
        {/* Ambient glows */}
        <div
          className={`absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full blur-[120px] ${transitionCls} ${
            mode === "dark" ? "bg-[#8B31FF]/10" : "bg-[#8B31FF]/5"
          }`}
        />
        <div
          className={`absolute bottom-0 right-1/4 w-[600px] h-[600px] rounded-full blur-[100px] ${transitionCls} ${
            mode === "dark" ? "bg-[#8B31FF]/5" : "bg-[#8B31FF]/3"
          }`}
        />
      </div>

      {/* ── Header ── */}
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 z-10">
        <div className="mx-auto max-w-3xl text-center mb-24">

          {/* Badge */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
            className={[
              "inline-flex items-center gap-2 px-4 py-1.5 rounded-full border",
              transitionCls,
              mode === "dark"
                ? "border-[#8B31FF]/30 bg-[#8B31FF]/10 backdrop-blur-md"
                : "border-[#8B31FF]/20 bg-white/50",
            ].join(" ")}
          >
            <span className="relative flex h-2 w-2">
              <span
                className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                  mode === "dark" ? "bg-[#8B31FF]" : "bg-[#8B31FF]"
                }`}
              />
              <span
                className={`relative inline-flex rounded-full h-2 w-2 ${
                  mode === "dark" ? "bg-[#8B31FF]" : "bg-[#8B31FF]"
                }`}
              />
            </span>
            <span
              className={`text-xs font-semibold tracking-widest uppercase ${transitionCls} ${
                mode === "dark" ? "text-[#8B31FF]" : "text-[#8B31FF]"
              }`}
            >
              Our Expertise
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h2
            custom={1}
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.4 }}
            className={`mt-5 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight leading-[1.15] ${transitionCls} ${
              mode === "dark" ? "text-white" : "text-gray-900"
            }`}
          >
            Comprehensive <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B31FF] to-[#8B31FF]">
              Digital Solutions
            </span>
          </motion.h2>

          {/* Subtext */}
          <motion.p
            custom={2}
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.4 }}
            className={`text-base sm:text-lg leading-relaxed max-w-2xl mx-auto ${transitionCls} ${
              mode === "dark" ? "text-gray-400" : "text-gray-600"
            }`}
          >
            We combine strategy, design, and technology to build products that drive real
            business results. Explore how we can help you grow.
          </motion.p>
        </div>
      </div>

      {/* ── Cards grid ── */}
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 z-10 mt-24">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {services.map((service, index) => (
            <ServiceCard
              key={service._id || index}
              service={service}
              index={index}
              mode={mode}
            />
          ))}
        </div>

        {services.length === 0 && !loading && (
          <motion.div
            variants={fadeIn}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className={`text-center py-12 ${mode === "dark" ? "text-gray-500" : "text-gray-400"}`}
          >
            No services found.
          </motion.div>
        )}
      </div>

      {/* ── Marquee strip ── */}
      <div className="relative z-10">
        <MarqueeBlock />
      </div>
    </section>
  );
};

export default ServicesSection;
